import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { errMsg, notifyError } from "@/lib/appsApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", phone: "", organisation: "", message: "" };

const Field = ({ label, name, value, onChange, type = "text", required, textarea }) => {
  const cls = "peer w-full border-0 border-b border-slate-300 bg-transparent px-0 pb-3 pt-6 text-base text-ink outline-none transition-colors placeholder:text-transparent focus:border-sky-600";
  const props = { id: name, name, value, onChange, required, placeholder: label, "data-testid": `contact-input-${name}` };
  return (
    <label className="relative block" htmlFor={name}>
      {textarea ? <textarea rows={4} className={`${cls} resize-none`} {...props} /> : <input type={type} className={cls} {...props} />}
      <span className="pointer-events-none absolute left-0 top-0 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors peer-focus:text-sky-700">
        {label}{required && " *"}
      </span>
    </label>
  );
};

const details = [
  { icon: Phone, label: "Call", value: "+61 481 418 199", href: "tel:+61481418199", id: "phone" },
  { icon: Mail, label: "Email", value: "sales@AeroSoftSolutions.com.au", href: "mailto:sales@AeroSoftSolutions.com.au", id: "email" },
  { icon: MapPin, label: "Registered office", value: "Level 1, 63-67 Ann St, Surry Hills NSW 2010 Australia", id: "office" },
];

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received — we'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      notifyError(`${errMsg(err, "Sending your enquiry")} You can also email sales@AeroSoftSolutions.com.au directly.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-[#F4F9FD] py-24 lg:py-32" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>03 — Contact</Eyebrow>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-[-0.03em] text-ink sm:text-5xl">
              Tell us the need. <span className="font-serif font-normal italic text-sky-600">Tell us the pace.</span>
            </h2>
          </Reveal>
          <div className="mt-12 space-y-3">
            {details.map((d, i) => {
              const Icon = d.icon;
              const Tag = d.href ? "a" : "div";
              return (
                <Reveal key={d.id} delay={0.08 * i}>
                  <Tag href={d.href} className="group flex items-center gap-4 rounded-3xl border border-sky-100 bg-white p-5 transition-colors duration-300 hover:border-sky-400" data-testid={`contact-detail-${d.id}`}>
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700 transition-colors duration-300 group-hover:bg-sky-600 group-hover:text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{d.label}</span>
                      <span className="block break-words text-sm font-medium text-ink sm:text-base">{d.value}</span>
                    </span>
                  </Tag>
                </Reveal>
              );
            })}
          </div>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} className="rounded-[32px] bg-white p-7 shadow-[0_40px_100px_-60px_rgba(11,79,138,0.5)] sm:p-12" data-testid="contact-form">
            <div className="grid gap-8 sm:grid-cols-2">
              <Field label="Full name" name="name" value={form.name} onChange={onChange} required />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
              <Field label="Organisation" name="organisation" value={form.organisation} onChange={onChange} />
            </div>
            <div className="mt-8">
              <Field label="How can we help?" name="message" value={form.message} onChange={onChange} required textarea />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-7 py-4 text-sm font-medium text-white transition-colors hover:bg-sky-700 disabled:opacity-60 sm:w-auto"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {sending ? "Sending" : "Send enquiry"}
              {!sending && <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
